/**
 * PIN policy — one rule, every place a PIN is set.
 *
 * A customer PIN is exactly 4 digits (Jon, 2026-10-25). The kiosk PIN is a
 * separate secret and stays 4-6. Before 2026-10-25 the stores accepted 4-6, and
 * the reset-PIN page let a 6-digit PIN through that the 4-box sign-in could not
 * type; stored 6-digit PINs still verify, and the sign-in box lets them be typed.
 *
 * Buyers sign in with phone + PIN. A 2026-09-17 audit of production
 * found 40 of 132 PIN-bearing accounts using a PIN from a 22-entry guess list,
 * and 24 of those were real customers (1234 sixteen times, then 0000, 2222,
 * 1111, 6969, 2580, 1122, 9999). Per-account lockout (5 attempts, then a 5-10
 * minute backoff) blunts spraying, but it cannot help when the first guess is
 * right.
 *
 * So the obvious PINs are refused at the moment one is chosen. This does not
 * retire the weak PINs already out there — those age out as customers reset —
 * it stops the set growing.
 *
 * Deliberately NOT a strength meter: a 4-digit PIN has 10,000 combinations and
 * dressing that up as "strong" would be a lie. The goal is only to knock out
 * the handful of values a person guesses first.
 */

/** Digits-only, 4 to 6 long. The KIOSK PIN's format; a customer PIN is CUSTOMER_PIN_LENGTH. */
export const PIN_FORMAT = /^\d{4,6}$/;

/**
 * Values people reach for first: keypad shapes (2580 is the middle column,
 * 1470/3690 the outer ones), years, and culturally common codes. Repeated
 * digits and runs are caught structurally below, so they are not listed here.
 */
const BLOCKLIST = new Set([
  '2580', '0852', '1470', '3690', '1379', '7913',
  '6969', '4200', '1122', '2211', '1004', '2468', '1357', '1379',
  '1984', '2000', '2020', '2021', '2022', '2023', '2024', '2025', '2026',
  '1969', '1990', '1991', '1992', '1993', '1994', '1995',
  '123456', '654321', '111222', '121212', '112233', '696969', '123123',
  '159753', '147258', '258456',
]);

/** Every digit the same: 0000, 1111, 999999. */
function isAllSameDigit(pin: string): boolean {
  return new Set(pin.split('')).size === 1;
}

/** A run of consecutive digits either way, with wraparound: 1234, 4321, 8901. */
function isSequential(pin: string): boolean {
  const step = (a: string, b: string) => (Number(b) - Number(a) + 10) % 10;
  const first = step(pin[0], pin[1]);
  if (first !== 1 && first !== 9) return false;
  for (let i = 1; i < pin.length - 1; i++) {
    if (step(pin[i], pin[i + 1]) !== first) return false;
  }
  return true;
}

/** Two digits alternating: 1212, 3737, 121212. */
function isAlternatingPair(pin: string): boolean {
  if (pin.length % 2 !== 0) return false;
  const a = pin[0];
  const b = pin[1];
  if (a === b) return true; // already caught by isAllSameDigit, harmless here
  for (let i = 0; i < pin.length; i++) {
    if (pin[i] !== (i % 2 === 0 ? a : b)) return false;
  }
  return true;
}

/** The same half twice: 123123, 4545 (4545 is also alternating), 246246. */
function isRepeatedHalf(pin: string): boolean {
  if (pin.length % 2 !== 0) return false;
  const half = pin.length / 2;
  return pin.slice(0, half) === pin.slice(half);
}

/**
 * True when a PIN is one of the obvious ones. Assumes PIN_FORMAT already
 * passed; a malformed string is not this function's business.
 */
export function isWeakPin(pin: string): boolean {
  return (
    BLOCKLIST.has(pin) ||
    isAllSameDigit(pin) ||
    isSequential(pin) ||
    isAlternatingPair(pin) ||
    isRepeatedHalf(pin)
  );
}

/**
 * What the buyer is told. Names the actual shapes rather than saying "too
 * weak", so the next attempt is likelier to pass.
 */
export const WEAK_PIN_MESSAGE =
  'Please choose a less predictable PIN — avoid repeated digits (1111), runs (1234), and repeating pairs (1212).';

/** Convenience for route handlers: null when acceptable, else the message. */
export function validatePin(pin: unknown): string | null {
  if (typeof pin !== 'string' || !PIN_FORMAT.test(pin)) {
    return 'PIN must be 4-6 digits (numbers only)';
  }
  return isWeakPin(pin) ? WEAK_PIN_MESSAGE : null;
}

/** A customer PIN is exactly this many digits — the one rule every store path shares. */
export const CUSTOMER_PIN_LENGTH = 4;

/**
 * Longest PIN the sign-in box lets a buyer type. Wider than the store rule on
 * purpose: a 6-digit PIN saved before 2026-10-25 must still be enterable.
 */
export const CUSTOMER_PIN_MAX_ENTRY_LENGTH = 6;

const CUSTOMER_PIN_FORMAT = new RegExp(`^\\d{${CUSTOMER_PIN_LENGTH}}$`);

export function isValidCustomerPin(pin: unknown): pin is string {
  return typeof pin === 'string' && CUSTOMER_PIN_FORMAT.test(pin);
}

export const CUSTOMER_PIN_FORMAT_MESSAGE = `PIN must be exactly ${CUSTOMER_PIN_LENGTH} digits (numbers only)`;

/** For every route that STORES a customer PIN. Verify routes must not use it. */
export function validateCustomerPin(pin: unknown): string | null {
  if (!isValidCustomerPin(pin)) return CUSTOMER_PIN_FORMAT_MESSAGE;
  return isWeakPin(pin) ? WEAK_PIN_MESSAGE : null;
}
